import { isAxiosError, type AxiosResponse } from 'axios';
import { type GetServerSidePropsContext } from 'next';

import axiosServerCreator from '@framework/api/apiCalls.server';
import { api } from '@framework/constants/endpoints';
import { HttpStatusCode } from '@framework/constants/enums/http';
import { camelizeKeys } from '@framework/utils/camelize.utils';

import { type ApiResponse } from '@type/api';
import { type User } from '@type/members';
import type { ROOM_FLAGS, GargoyleSwitches, Room } from '@type/rooms';

export const serverUrlBuilder = (url: string): string => `${process.env.API_INTERNAL_URL}${url}`;

export const getServerSideActiveRoom = async (
    context: GetServerSidePropsContext
): Promise<Room> => {
    const {
        query: { room = '' }
    } = context;
    const axiosServer = axiosServerCreator(context);
    try {
        const {
            data: { results: activeRoom }
        } = await axiosServer.get<ApiResponse<Room>>(serverUrlBuilder(api.room(String(room))));
        return activeRoom;
    } catch (error) {
        if (isAxiosError(error) && error.status === HttpStatusCode.NotFound) {
            throw new Error(`Room with slug ${room} not found`);
        }
        throw error;
    }
};

export const getServerSideLoggedInUser = async (
    context: GetServerSidePropsContext,
    activeRoom?: Room
) => {
    let room = activeRoom;

    if (!activeRoom) {
        room = await getServerSideActiveRoom(context);
    }

    const {
        organisation: { id: organisationId }
    } = room as Room;

    const axiosServer = axiosServerCreator(context);
    const {
        data: { results: loggedUser }
    }: AxiosResponse<ApiResponse<User>> = await axiosServer.get(serverUrlBuilder(api.user()), {
        params: {
            organisationId
        }
    });

    return loggedUser;
};

export const getServerSideLoggedInMemberPermissions = async (
    context: GetServerSidePropsContext,
    activeRoom?: Room
) => {
    let room = activeRoom;

    if (!activeRoom) {
        room = await getServerSideActiveRoom(context);
    }

    return room?.memberPermissions;
};

export const getServerSideRoomFlags = async (
    context: GetServerSidePropsContext,
    activeRoom?: Room
): Promise<Record<ROOM_FLAGS, boolean>> => {
    let room = activeRoom;

    if (!activeRoom) {
        room = await getServerSideActiveRoom(context);
    }

    const axiosServer = axiosServerCreator(context);
    const {
        data: { results }
    }: AxiosResponse<ApiResponse<GargoyleSwitches>> = await axiosServer.get(
        serverUrlBuilder(api.flags()),
        {
            params: {
                roomId: room?.id,
                state: 'pending'
            }
        }
    );
    const { gargoyleSwitches } = results || { gargoyleSwitches: [] };
    const gargoyleEntries = gargoyleSwitches.map(({ active, key }) => [key, active]);

    return camelizeKeys(Object.fromEntries(gargoyleEntries));
};
